import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import emailjs from '@emailjs/browser';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    subject: '',
    message: ''
    // priority: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const inquiryTypes = [
    { value: 'Enrollment', label: 'Course Enrollment' },
    { value: 'Institutional', label: 'Institutional Partnership' },
    { value: 'Technical', label: 'Technical Support' },
    { value: 'Billing', label: 'Billing & Payments' },
    { value: 'General', label: 'General Inquiry' }
  ];

  // const priorityOptions = [
  //   { value: 'Low Priority', label: 'Low Priority' },
  //   { value: 'Medium Priority', label: 'Medium Priority' },
  //   { value: 'High Priority', label: 'High Priority' },
  //   { value: 'URGENT', label: 'Urgent' }
  // ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.inquiryType) newErrors.inquiryType = 'Please select an inquiry type';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (formData.message.length < 10) newErrors.message = 'Message must be at least 10 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await emailjs.send(
        "service_jgpq5lf",      // Replace with your EmailJS Service ID
        "template_8g8rxdr",     // Replace with your EmailJS Template ID
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          inquiry_type: formData.inquiryType,
          subject: formData.subject,
          message: formData.message
          // priority: formData.priority,
        },
        "g5a82t4oDjG6o7abG"        // Replace with your EmailJS Public Key
      );

      alert("✅ Your message has been sent successfully!");

      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        inquiryType: '',
        subject: '',
        message: ''
        // priority: 'medium',
      });
      setErrors({});
    } catch (error) {
      console.error("EmailJS Error:", error);
      alert(`❌ Something went wrong: ${error.text || error.message || "Unknown error"}`);
    }
    setIsSubmitting(false);
  };

  return (
    <section className="py-16 lg:py-24 bg-card/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Send Us a Message
          </h2>
          <p className="text-lg text-muted-foreground">
            Fill out the form below and we'll get back to you as soon as possible
          </p>
        </div>

        <div className="holographic-card p-8 lg:p-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                error={errors.name}
                required
              />
              
              <Input
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                error={errors.email}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Phone Number"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 (555) 123-4567"
                description="Optional - for urgent inquiries"
              />
              
              <Select
                label="Inquiry Type"
                options={inquiryTypes}
                value={formData.inquiryType}
                onChange={(value) => handleSelectChange('inquiryType', value)}
                placeholder="Select inquiry type"
                error={errors.inquiryType}
                required
              />
            </div>

            {/* Subject and Priority */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <Input
                  label="Subject"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Brief description of your inquiry"
                  error={errors.subject}
                  required
                />
              </div>
              {/*<Select
                label="Priority Level"
                options={priorityOptions}
                value={formData.priority}
                onChange={(value) => handleSelectChange('priority', value)}
                description="How urgent is your inquiry?"
              /> */}
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message <span className="text-destructive">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                placeholder="Please provide detailed information about your inquiry..."
                required
              />
              {errors.message && (
                <p className="mt-1 text-sm text-destructive">{errors.message}</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Minimum 10 characters. Be as specific as possible for faster resolution.
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <Button
                type="submit"
                variant="default"
                className="morphing-cta flex-1"
                loading={isSubmitting}
                iconName="Send"
                iconPosition="right"
              >
                {isSubmitting ? 'Sending Message...' : 'Send Message'}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    inquiryType: '',
                    subject: '',
                    message: ''
                    // priority: 'medium'
                  });
                  setErrors({});
                }}
              >
                Clear Form
              </Button>
            </div>

            {/* Additional Info
            <div className="mt-8 p-4 bg-muted/20 rounded-lg border border-border">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-accent mt-0.5" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">Response Time Commitment</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• <strong>Urgent:</strong> Within 30 minutes during business hours</li>
                    <li>• <strong>High Priority:</strong> Within 2 hours</li>
                    <li>• <strong>Medium/Low Priority:</strong> Within 24 hours</li>
                  </ul>
                </div>
              </div>
            </div> */}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;